import { useEmpresaLogo } from "@/components/pageFragment/empresaLogo";
import { cx } from "@/ui/cx";
import { MaterialCommunityIcons, MaterialIcons } from "@/ui/icons";
import { Image } from "@/ui/Image";
import { memo, useState, type CSSProperties } from "react";
import logoTirvuHorizontalBranco from "@/assets/images/logo_tirvu_horizontal_branco.png";
import s from "./HomeSkeleton.module.css";

// Skeleton da Home num componente PRÓPRIO e LEVE, para o placeholder da rota
// (app/(tabs)/home.tsx) abrir INSTANTANEAMENTE já com o skeleton — enquanto a
// HomeScreen (componente gigante) monta por baixo. NÃO toca na Home.tsx (que segue
// no estado original, com a foto funcionando).

const BACKGROUND = "#f2f7f9";

// Pulso ÚNICO compartilhado: 1 Animated.Value + 1 loop nativo.
// isInteraction:false é OBRIGATÓRIO — um loop infinito que segura interaction handle
// trava o InteractionManager.runAfterInteractions de toda a app.
// WEB: o pulso (0.3↔0.7, 800 ms + 800 ms) é um @keyframes CSS. Para todos os blocos
// ficarem EM FASE (como o valor compartilhado do RN), cada bloco começa com
// animation-delay negativo igual à fase atual do ciclo global de 1600 ms.
const CICLO_PULSO_MS = 1600;
const fasePulsoAtual = () =>
  typeof performance !== "undefined" ? -(performance.now() % CICLO_PULSO_MS) : 0;

type SkeletonItemProps = {
  width?: number | string;
  height?: number | string;
  borderRadius?: number;
  style?: CSSProperties;
};

const SkeletonItem = memo(function SkeletonItem({
  width,
  height,
  borderRadius = 8,
  style,
}: SkeletonItemProps) {
  const [delay] = useState(fasePulsoAtual);
  return (
    <div
      className={s.skeletonItem}
      style={{
        width,
        height,
        borderRadius,
        animationDelay: `${delay}ms`,
        ...style,
      }}
    />
  );
});

const ProfileSkeleton = memo(function ProfileSkeleton() {
  return (
    <div className={cx(s.coluna, s.profile)}>
      <div className={s.profileLinha}>
        <div className={s.avatarSombra}>
          <div className={s.avatar}>
            <SkeletonItem width={95} height={95} borderRadius={47.5} />
          </div>
        </div>

        <div className={s.pilulas}>
          <div className={s.pilulaNome}>
            <SkeletonItem width={140} height={16} borderRadius={4} />
          </div>
          <div className={s.pilulaCargo}>
            <SkeletonItem
              width={100}
              height={12}
              borderRadius={4}
              style={{ backgroundColor: "#3a5a68" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

const BodySkeleton = memo(function BodySkeleton() {
  return (
    <div className={s.body}>
      {/* Ultimos Registros */}
      <div className={s.gap10}>
        <div className={s.sectionHeaderUltimos}>
          <SkeletonItem width={140} height={20} />
          <SkeletonItem width={60} height={14} />
        </div>
        <div className={s.miniCardsLinha}>
          {[1, 2, 3].map((i) => (
            <div key={i} className={s.miniCard}>
              <div className={s.linhaCentro10}>
                <SkeletonItem width={34} height={34} borderRadius={10} />
                <div className={s.miniCardTextos}>
                  <SkeletonItem width="100%" height={10} borderRadius={4} />
                  <SkeletonItem width="70%" height={18} borderRadius={4} />
                  <SkeletonItem width="60%" height={10} borderRadius={4} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hero */}
      <div className={cx(s.heroCard, s.heroCardSkeleton)}>
        <div className={s.heroTopo}>
          <SkeletonItem width={120} height={20} borderRadius={10} />
          <SkeletonItem width={50} height={20} />
        </div>
        <div className={s.heroMeio}>
          <SkeletonItem width={52} height={52} borderRadius={26} />
          <div className={s.flex1Gap8}>
            <SkeletonItem width="60%" height={20} />
            <div className={s.heroLocal}>
              <SkeletonItem width={14} height={14} borderRadius={7} />
              <SkeletonItem width="40%" height={14} />
            </div>
          </div>
          <div className={s.heroDireita}>
            <SkeletonItem width={60} height={22} />
            <SkeletonItem width={40} height={12} />
          </div>
        </div>
        <div className={s.mb16}>
          <SkeletonItem width="100%" height={6} borderRadius={3} style={{ marginBottom: 8 }} />
          <SkeletonItem width="80%" height={12} />
        </div>
        <SkeletonItem width="100%" height={45} borderRadius={14} />
      </div>

      {/* Informativos */}
      <div className={s.informativos}>
        <div className={s.sectionHeader}>
          <SkeletonItem width={100} height={20} />
        </div>
        <div className={cx(s.storyCard, s.storyCardSkeleton)}>
          <div className={s.storyConteudo}>
            <div className={s.storyTopo}>
              <SkeletonItem width={38} height={38} borderRadius={11} />
              <SkeletonItem width="50%" height={20} />
              <SkeletonItem width={28} height={20} borderRadius={14} />
            </div>
            <div className={s.storyRodape}>
              <SkeletonItem width="70%" height={20} />
              <SkeletonItem width="90%" height={12} />
              <SkeletonItem width="30%" height={30} borderRadius={10} style={{ marginTop: 5 }} />
            </div>
          </div>
        </div>
      </div>

      {/* Saldo */}
      <div className={s.saldoCard}>
        <div className={s.saldoHeader}>
          <SkeletonItem width={34} height={34} borderRadius={10} />
          <div className={s.flex1Gap4}>
            <SkeletonItem width="60%" height={14} />
            <SkeletonItem width="40%" height={10} />
          </div>
          <SkeletonItem width={40} height={20} borderRadius={10} />
        </div>
        <div className={s.saldoValor}>
          <SkeletonItem width={80} height={32} />
          <SkeletonItem width={30} height={12} />
        </div>
        <div className={s.saldoRodape}>
          <div className={s.linhaCentro6}>
            <SkeletonItem width={8} height={8} borderRadius={4} />
            <SkeletonItem width={80} height={12} />
          </div>
          <div className={s.linhaCentro6}>
            <SkeletonItem width={8} height={8} borderRadius={4} />
            <SkeletonItem width={80} height={12} />
          </div>
        </div>
      </div>

      <SkeletonItem width="100%" height={1} />

      {/* Gráfico Semanal */}
      <div className={s.weekCard}>
        <div className={s.weekTopo}>
          <div className={s.flex1Gap4}>
            <SkeletonItem width="40%" height={14} />
            <SkeletonItem width="60%" height={12} />
          </div>
          <SkeletonItem width={60} height={40} borderRadius={8} />
        </div>
        <div className={s.weekBarras}>
          {[60, 40, 75, 50, 80, 45, 65].map((h, i) => (
            <div key={i} className={s.weekColuna}>
              <div className={s.weekTrilho} style={{ backgroundColor: BACKGROUND }}>
                <SkeletonItem width="100%" height={h} borderRadius={6} />
              </div>
              <SkeletonItem width={20} height={10} style={{ marginTop: 6 }} />
            </div>
          ))}
        </div>
      </div>

      {/* Atalhos */}
      <div>
        <div className={s.sectionHeader}>
          <SkeletonItem width={120} height={20} />
        </div>
        <div className={s.atalhosGrid}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={s.atalhoItem}>
              <div className={s.atalhoBtn}>
                <SkeletonItem width={44} height={44} borderRadius={12} />
                <SkeletonItem width={50} height={12} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

/**
 * Página COMPLETA de skeleton da Home (header com chrome real + perfil + corpo),
 * espelhando o PageFragment. Usada pelo placeholder da rota para a tela ABRIR já com
 * o skeleton instantaneamente.
 */
export const HomeSkeletonPage = memo(function HomeSkeletonPage() {
  const empresaLogo = useEmpresaLogo();
  return (
    <div className={s.pagina}>
      {/* Header espelhando o PageFragment (logo empresa + TIRVU + ícones), estático. */}
      {/* WEB: insets.top + 10 → calc(var(--safe-top) + 10px) no CSS. */}
      <div className={s.header}>
        <div className={cx(s.coluna, s.headerLinha)}>
          <div className={s.linhaCentro}>
            <div className={s.empresaBox}>
              <Image source={empresaLogo} className={s.empresaLogo} resizeMode="contain" />
            </div>
            <MaterialIcons
              name="keyboard-arrow-down"
              size={20}
              color="#fff"
              style={{ marginLeft: 4 }}
            />
          </div>
          <img src={logoTirvuHorizontalBranco} alt="" className={s.logoTirvu} draggable={false} />
          <div className={s.headerIcones}>
            {/* WEB: botão "atualizar" do cabeçalho real — só aparece no desktop (CSS). */}
            <div className={s.iconeAtualizar}>
              <MaterialIcons name="refresh" size={22} color="#fff" />
            </div>
            <MaterialCommunityIcons name="chat-outline" size={24} color="#fff" />
            <MaterialCommunityIcons name="bell-outline" size={24} color="#fff" />
          </div>
        </div>
      </div>
      <ProfileSkeleton />
      {/* WEB: ScrollView com scrollEnabled={false} → contêiner sem rolagem. */}
      <div className={s.scroll}>
        <div className={cx(s.coluna, s.scrollConteudo)}>
          <BodySkeleton />
        </div>
      </div>
    </div>
  );
});
